/**
 * Field group definitions for the Hybrid Timescale Reporting feature.
 *
 * Defines the canonical groups, their visual properties (color, icon),
 * and the Display_Field_Keys belonging to each group.
 */

export interface FieldDefinition {
  /** The Display_Field_Key used in API requests and responses */
  key: string
  /** Human-readable label for display in the UI (used in table headers and field selector) */
  label: string
}

export interface FieldGroup {
  /** Unique identifier for the group */
  key: string
  /** Human-readable group label */
  label: string
  /** Icon name for the group (e.g. "bolt", "plug") */
  icon: string
  /** Color token for the group (e.g. "teal-deep", "blue") */
  color: string
  /** Ordered list of fields belonging to this group */
  fields: FieldDefinition[]
}

/**
 * The 6 canonical field groups in their required display order.
 * Period is always included and has no color/icon for the selector.
 */
export const FIELD_GROUPS: FieldGroup[] = [
  {
    key: "period",
    label: "Period",
    icon: "",
    color: "",
    fields: [
      { key: "bucket_start", label: "Start date" },
      { key: "bucket_end", label: "End date" },
    ],
  },
  {
    key: "power_generated",
    label: "Power Generated",
    icon: "bolt",
    color: "teal-deep",
    fields: [
      { key: "generation_power_average", label: "Average Power (kW)" },
      { key: "generation_power_peak", label: "Peak Power (kW)" },
      { key: "generation_power_peak_time", label: "Peak Time" },
      { key: "generation_energy", label: "Energy (kWh)" },
      { key: "generation_co2e", label: "CO₂e (kg)" },
      { key: "generation_co2e_per_kwh", label: "CO₂e (kg/kWh)" },
    ],
  },
  {
    key: "power_consumed",
    label: "Power Consumed",
    icon: "plug",
    color: "blue",
    fields: [
      { key: "load_power_average", label: "Average Power (kW)" },
      { key: "load_power_peak", label: "Peak Power (kW)" },
      { key: "load_power_peak_time", label: "Peak Time" },
      { key: "load_energy", label: "Energy (kWh)" },
    ],
  },
  {
    key: "hydrogen",
    label: "Hydrogen",
    icon: "flask",
    color: "cyan",
    fields: [
      { key: "hydrogen_weight_start", label: "Start Weight (kg)" },
      { key: "hydrogen_weight_end", label: "End Weight (kg)" },
      { key: "hydrogen_weight_consumed", label: "Weight Consumed (kg)" },
    ],
  },
  {
    key: "diesel",
    label: "Diesel",
    icon: "gas-pump",
    color: "amber",
    fields: [
      { key: "diesel_level", label: "Average Level (%)" },
      { key: "diesel_level_start", label: "Start Level (L)" },
      { key: "diesel_level_end", label: "End Level (L)" },
      { key: "diesel_fuel_consumed", label: "Fuel Consumed (L)" },
    ],
  },
  {
    key: "water",
    label: "Water",
    icon: "droplet",
    color: "blue",
    fields: [{ key: "water_level", label: "Average Level (%)" }],
  },
  {
    key: "waste",
    label: "Waste",
    icon: "recycle",
    color: "gray",
    fields: [{ key: "waste_level", label: "Average Level (%)" }],
  },
  {
    key: "energy_storage",
    label: "Energy Storage",
    icon: "battery",
    color: "green",
    fields: [
      { key: "battery_soc_average", label: "Average SoC (%)" },
      { key: "battery_soc_peak", label: "Max SoC (%)" },
      { key: "battery_soc_min", label: "Min SoC (%)" },
      { key: "battery_discharge_power_average", label: "Discharge Power (kW)" },
      { key: "battery_discharge_energy", label: "Discharge Energy (kWh)" },
      { key: "battery_charge_power_average", label: "Charge Power (kW)" },
      { key: "battery_charge_energy", label: "Charge Energy (kWh)" },
    ],
  },
  {
    key: "electrolyser",
    label: "Electrolyser",
    icon: "bolt",
    color: "cyan",
    fields: [
      { key: "hydrogen_flow_rate_average", label: "Flow Rate Avg (NL/hour)" },
      { key: "hydrogen_produced", label: "Produced (NL)" },
      { key: "hydrogen_produced_total", label: "Produced Total (NL)" },
    ],
  },
]
